import { useState } from "react";
import { MessageSquareHeart, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = ["General", "Feature request", "Bug", "Content / scripture", "Other"];

export default function Feedback() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [category, setCategory] = useState("General");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const inputCls =
    "w-full bg-transparent border border-[#D9D4CC] px-4 py-3 text-[#2C2A29] placeholder-[#A39C90] rounded-sm focus:ring-2 focus:ring-[#EAE6DF] focus:border-[#B7AE9F] focus:outline-none transition-all duration-300";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/feedback", { name, email, category, message });
      setDone(true);
      setMessage("");
      toast.success("Thank you — your feedback has been received.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-6 sm:px-8 pt-24 pb-24 text-center" data-testid="feedback-success">
        <div className="inline-flex items-center justify-center w-14 h-14 border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm mb-6">
          <Check className="w-6 h-6 text-[#2C2A29]" />
        </div>
        <h1 className="font-heading text-3xl text-[#2C2A29] tracking-tight">Thank you</h1>
        <p className="font-decree text-xl text-[#4A4744] mt-4">
          Your feedback has been received and will be reviewed by our team.
        </p>
        <button
          data-testid="feedback-another"
          onClick={() => setDone(false)}
          className="mt-8 px-6 py-3 border border-[#D9D4CC] text-[#2C2A29] rounded-sm hover:bg-[#EAE6DF] transition-colors duration-300"
        >
          Send more feedback
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-6 sm:px-8 pt-16 pb-24" data-testid="feedback-page">
      <div className="inline-flex items-center justify-center w-14 h-14 border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm mb-6">
        <MessageSquareHeart className="w-6 h-6 text-[#2C2A29]" strokeWidth={1.5} />
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] tracking-tight">We'd love your feedback</h1>
      <p className="font-decree text-lg text-[#4A4744] mt-3">
        Tell us what's working, what could be better, or what you'd love to see next.
      </p>

      <form onSubmit={submit} className="space-y-4 mt-8">
        <input data-testid="feedback-name" className={inputCls} placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
        <input data-testid="feedback-email" type="email" className={inputCls} placeholder="Your email (optional)" value={email} onChange={(e) => setEmail(e.target.value)} />
        <select data-testid="feedback-category" className={inputCls} value={category} onChange={(e) => setCategory(e.target.value)}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <textarea
          data-testid="feedback-message"
          className={`${inputCls} min-h-[140px] resize-y`}
          placeholder="Share your thoughts…"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          required
        />
        <button
          data-testid="feedback-submit"
          type="submit"
          disabled={busy || !message.trim()}
          className="w-full px-6 py-3.5 bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300 disabled:opacity-50"
        >
          {busy ? "Sending…" : "Send feedback"}
        </button>
      </form>
    </div>
  );
}
